import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FILTER_CATEGORIES, FEATURE_FLAGS } from "../constants";
import CategoryButton from "../components/CategoryButton";
import ActivityCard from "../components/ActivityCard";
import UserSearchModal from "../components/UserSearchModal";
import {
  PlusIcon,
  BellIcon,
  MapIcon,
  SparklesIcon,
  SearchIcon,
} from "../components/icons/FeatherIcons";
import { AppLogoIcon } from "../components/icons/AppLogoIcon";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityFeedData,
  useActivityNotificationsData,
} from "../contexts/ActivityContext";
import { type ActivityRequest } from "../types";
import { motion, AnimatePresence } from "framer-motion";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { usePageTracking } from "../hooks/usePageTracking";
import { createPortal } from "react-dom";
import { crashlytics } from "../services/firebase";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const HomePage: React.FC = () => {
  usePageTracking("Home", "HomePage");
  const { currentUser } = useAuth();
  const activityFeedData = useActivityFeedData();
  const activityNotificationsData = useActivityNotificationsData();
  const activityActions = useActivityActions();
  const location = useLocation();
  // Safely extract context values with guaranteed defaults to prevent hook dependency issues
  const activities = activityFeedData.activities || [];
  const getUnreadNotificationCount =
    activityNotificationsData.getUnreadNotificationCount;
  const passActivity = activityActions.passActivity;
  const isLoading = activityFeedData.isLoading ?? true;
  const refreshActivities = activityActions.refreshActivities;
  const refreshing = activityFeedData.refreshing ?? false;
  const loadMoreActivities = activityActions.loadMoreActivities;
  const isLoadingMore = activityFeedData.isLoadingMore ?? false;
  const hasMoreActivities = activityFeedData.hasMoreActivities ?? false;

  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isLoadingMoreActivities, setIsLoadingMoreActivities] = useState(false);
  const [newActivitiesCount, setNewActivitiesCount] = useState(0);
  const [isNewActivitiesBannerDismissed, setIsNewActivitiesBannerDismissed] =
    useState(false);
  const queryCategory = activeCategory === "all" ? undefined : activeCategory;

  // Pull to refresh states
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartYRef = useRef<number | null>(null);
  const touchStartScrollYRef = useRef<number>(0);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const SCROLL_POSITION_TOLERANCE = 5; // Very strict - only 5px from top

  // Reset pull states on component mount/unmount
  useEffect(() => {
    // Reset states when component mounts
    setPullDistance(0);
    setIsPulling(false);
    touchStartYRef.current = null;
    touchStartScrollYRef.current = 0;

    return () => {
      // Cleanup on unmount
      setPullDistance(0);
      setIsPulling(false);
      touchStartYRef.current = null;
      touchStartScrollYRef.current = 0;
    };
  }, []);

  // Enhanced haptic feedback for Android Pixel 7a
  const triggerHapticFeedback = useCallback(async () => {
    try {
      // Light haptic feedback - single gentle vibration
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (capacitorError) {
      // Fallback: Web Vibration API with lighter pattern
      if ("vibrate" in navigator && typeof navigator.vibrate === "function") {
        navigator.vibrate([100]); // Single short vibration
      }
    }
  }, []);

  // Track refresh completion and trigger vibration
  const previousRefreshingRef = useRef(refreshing);

  useEffect(() => {
    // If refreshing changed from true to false, refresh completed
    if (previousRefreshingRef.current === true && refreshing === false) {
      triggerHapticFeedback();
    }
    previousRefreshingRef.current = refreshing;
  }, [refreshing, triggerHapticFeedback]);

  const filteredActivities = useMemo(() => {
    if (!currentUser) return [];

    if (activeCategory === "all") {
      return activities;
    }

    return activities.filter((activity) => activity.category === activeCategory);
  }, [activities, currentUser, activeCategory]);

  // Hide newly arrived items until user taps the banner
  const visibleActivities = useMemo(() => {
    if (newActivitiesCount <= 0) return filteredActivities;
    return filteredActivities.slice(newActivitiesCount);
  }, [filteredActivities, newActivitiesCount]);

  const unreadNotifications =
    currentUser && getUnreadNotificationCount
      ? getUnreadNotificationCount(currentUser.id)
      : 0;

  const handlePassActivity = (activityId: string) => {
    if (passActivity) {
      passActivity(activityId);
    }
  };

  // Local paging state
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Detect new activities (only when prepended, not when loading more at the end)
  const firstActivityIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only count as "new" if activities are added at the beginning (real-time updates)
    // Not when loading more at the end (pagination)
      if (activities.length > 0) {
        const firstActivityId = activities[0].id;

      // If this is the first load, just store the ID
      if (firstActivityIdRef.current === null) {
        firstActivityIdRef.current = firstActivityId;
        return;
      }

      // If the first activity ID changed, new activities were prepended
      if (firstActivityId !== firstActivityIdRef.current) {
        // Count how many new activities were added at the start
        let newCount = 0;
        for (let i = 0; i < activities.length; i++) {
          if (activities[i].id === firstActivityIdRef.current) {
            newCount = i;
            break;
          }
        }

        if (newCount > 0) {
          // Accumulate when multiple batches arrive before the user views them
          setNewActivitiesCount((prev) => prev + newCount);
          setIsNewActivitiesBannerDismissed(false);
        }

        firstActivityIdRef.current = firstActivityId;
      }
    }
  }, [activities]);

  // Show more activities when user scrolls near bottom
  useEffect(() => {
    const mainContent = document.querySelector(".pull-to-refresh-container") as HTMLElement;
    if (!mainContent) return;

    const handleScroll = () => {
      const scrollY = mainContent.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = mainContent.scrollHeight;

      // Auto-load when near bottom
      if (scrollY + windowHeight >= docHeight - 200) {
        // First priority: show more already-loaded activities
        if (!isLoadingMoreActivities && visibleCount < visibleActivities.length) {
          setIsLoadingMoreActivities(true);
          setVisibleCount((prev) => {
            const newCount = Math.min(visibleActivities.length, prev + PAGE_SIZE);
            // Reset loading state after a brief delay
            setTimeout(() => setIsLoadingMoreActivities(false), 300);
            return newCount;
          });
        }
        // Second priority: fetch more from server if available and we've shown everything
        else if (!isLoadingMore && hasMoreActivities && visibleCount >= visibleActivities.length && loadMoreActivities) {
          loadMoreActivities({ category: queryCategory });
        }
      }
    };
    mainContent.addEventListener("scroll", handleScroll);
    return () => mainContent.removeEventListener("scroll", handleScroll);
  }, [
    filteredActivities.length,
    isLoadingMoreActivities,
    visibleCount,
    isLoadingMore,
    hasMoreActivities,
    loadMoreActivities,
    queryCategory,
    visibleActivities.length,
  ]);

  // Reset visibleCount only when category filter changes.
  // Do not reset on activities length changes, otherwise realtime prepends
  // (hidden behind the "x new" badge) can cause visible feed jumps.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory]);

  // Handle pull to refresh - touch start
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (searchModalOpen) return;

    // Find all possible scrollable elements
    const mainContent = document.querySelector(".pull-to-refresh-container") as HTMLElement;
    const mainElement = document.querySelector("main") as HTMLElement;
    const rootElement = document.getElementById("root") as HTMLElement;

    // Try multiple scroll position sources
    const mainScrollTop = mainContent?.scrollTop || 0;
    const mainElementScrollTop = mainElement?.scrollTop || 0;
    const rootScrollTop = rootElement?.scrollTop || 0;
    const windowScrollY = window.scrollY || 0;
    const pageYOffset = window.pageYOffset || 0;
    const docScrollTop = document.documentElement.scrollTop || 0;
    const bodyScrollTop = document.body.scrollTop || 0;

    // Use the first non-zero value, or 0 if all are zero
    const scrollY = mainScrollTop || mainElementScrollTop || rootScrollTop || windowScrollY || pageYOffset || docScrollTop || bodyScrollTop || 0;

    // Only allow pull-to-refresh when at the very top of the feed
    if (scrollY > SCROLL_POSITION_TOLERANCE) {
      touchStartYRef.current = null;
      setIsPulling(false);
      return;
    }

    const touchY = e.touches[0].clientY;
    touchStartYRef.current = touchY;
    touchStartScrollYRef.current = scrollY;
    setIsPulling(true);
  }, [searchModalOpen]);

  // Handle pull to refresh - touch move
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (searchModalOpen) return;
    if (!isPulling || touchStartYRef.current === null) return;

    // Find all possible scrollable elements
    const mainContent = document.querySelector(".pull-to-refresh-container") as HTMLElement;
    const mainElement = document.querySelector("main") as HTMLElement;
    const rootElement = document.getElementById("root") as HTMLElement;

    // Try multiple scroll position sources
    const mainScrollTop = mainContent?.scrollTop || 0;
    const mainElementScrollTop = mainElement?.scrollTop || 0;
    const rootScrollTop = rootElement?.scrollTop || 0;
    const windowScrollY = window.scrollY || 0;
    const pageYOffset = window.pageYOffset || 0;
    const docScrollTop = document.documentElement.scrollTop || 0;
    const bodyScrollTop = document.body.scrollTop || 0;

    // Use the first non-zero value, or 0 if all are zero
    const scrollY = mainScrollTop || mainElementScrollTop || rootScrollTop || windowScrollY || pageYOffset || docScrollTop || bodyScrollTop || 0;

    const currentY = e.touches[0].clientY;
    const startY = touchStartYRef.current;

    // Cancel if page has scrolled at all since touch start
    if (scrollY !== touchStartScrollYRef.current) {
      setIsPulling(false);
      setPullDistance(0);
      touchStartYRef.current = null;
      return;
    }

    const rawDistance = currentY - touchStartYRef.current;
    const distance = Math.max(0, rawDistance);
    const maxDistance = 120;

    // Cancel pull if fingers move upward
    if (rawDistance < -10) {
      setIsPulling(false);
      setPullDistance(0);
      touchStartYRef.current = null;
      return;
    }

    const newPullDistance = Math.min(distance, maxDistance);
    setPullDistance(newPullDistance);

    // Prevent default scrolling behavior
    if (distance > 0) {
      e.preventDefault();
    }
  }, [isPulling, searchModalOpen]);

  // Handle pull to refresh - touch end
  const handleTouchEnd = useCallback(() => {
    if (searchModalOpen) return;
    if (!isPulling) {
      setIsPulling(false);
      setPullDistance(0);
      touchStartYRef.current = null;
      touchStartScrollYRef.current = 0;
      return;
    }

    const threshold = 80;

    if (pullDistance > threshold && !refreshing) {
      // Trigger refresh - fetch latest activities
      if (refreshActivities) {
        refreshActivities({ category: queryCategory });
      }
      // Reset view to show newly loaded activities
      setNewActivitiesCount(0);
      setVisibleCount(PAGE_SIZE);
    }

    setIsPulling(false);
    setPullDistance(0);
    touchStartYRef.current = null;
    touchStartScrollYRef.current = 0;
  }, [
    isPulling,
    pullDistance,
    refreshing,
    searchModalOpen,
    refreshActivities,
    PAGE_SIZE,
    queryCategory,
  ]);

  // Add and remove touch listeners
  useEffect(() => {
    const mainContent = document.querySelector(".pull-to-refresh-container") as HTMLElement;
    if (!mainContent) return;

    // Use passive: false to allow preventDefault() to work
    mainContent.addEventListener("touchstart", handleTouchStart as any, { passive: false });
    mainContent.addEventListener("touchmove", handleTouchMove as any, { passive: false });
    mainContent.addEventListener("touchend", handleTouchEnd as any);

    return () => {
      mainContent.removeEventListener("touchstart", handleTouchStart as any);
      mainContent.removeEventListener("touchmove", handleTouchMove as any);
      mainContent.removeEventListener("touchend", handleTouchEnd as any);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  if (!currentUser) {
    return null;
  }

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      <header className="sticky top-0 z-20 bg-white px-2 sm:px-3 border-b border-gray-200 h-14 sm:h-16 flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <div
          onClick={() => navigate("/create")}
          role="button"
          tabIndex={0}
          aria-label="Create an activity with AI"
          className="flex-1 flex items-center bg-gray-100 h-9 sm:h-10 rounded-full cursor-pointer hover:bg-gray-200 transition-colors min-w-0"
        >
          <AppLogoIcon className="w-4 h-4 sm:w-5 sm:h-5 mx-2 sm:mx-3 text-primary flex-shrink-0" />
          <span className="text-gray-500 font-medium text-xs sm:text-sm truncate pr-2 sm:pr-3">
            Plan something fun with AI...
          </span>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <button
            onClick={() => setSearchModalOpen(true)}
            className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Search users"
          >
            <SearchIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          <Link
            to="/notifications"
            className="relative w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="View notifications"
          >
            <BellIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            {unreadNotifications > 0 && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 25 }}
                className={`absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 ${unreadNotifications > 99
                  ? "px-1 h-4 min-w-[18px] sm:px-1.5 sm:h-5 sm:min-w-[24px]"
                  : unreadNotifications > 9
                    ? "px-0.5 h-3.5 min-w-[16px] sm:px-1 sm:h-4 sm:min-w-[20px]"
                    : "w-4 h-4 sm:w-5 sm:h-5"
                  } text-xs bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full flex items-center justify-center border-2 border-white shadow-lg`}
              >
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </motion.span>
            )}
          </Link>
          <Link
            to="/map"
            aria-label="Open map view"
            className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            <MapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </Link>
        </div>
      </header>
      <section
        className={`sticky top-14 sm:top-16 bg-white z-10 border-b border-gray-200 transition-all duration-300 py-2 relative`}
      >
        <div className="flex space-x-2 overflow-x-auto hide-scrollbar px-4 py-2">
          {FILTER_CATEGORIES.map((cat) => (
            <CategoryButton
              key={cat.name}
              name={cat.name}
              Icon={cat.Icon}
              isActive={activeCategory === cat.value}
              onClick={() => setActiveCategory(cat.value)}
            />
          ))}
        </div>

        {/* New Activities Banner - floating overlay anchored under filters */}
        <AnimatePresence>
          {newActivitiesCount > 0 && !isNewActivitiesBannerDismissed && (
            <motion.div
              className="pointer-events-none absolute left-0 right-0 top-full transform translate-y-2 flex justify-center px-4"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gradient-to-r from-primary to-primary/80 text-white text-xs font-semibold shadow-lg shadow-primary/30 border border-primary/80"
              >
                <button
                  onClick={() => {
                    setNewActivitiesCount(0);
                    setIsNewActivitiesBannerDismissed(false);
                    setVisibleCount((prev) => Math.max(prev, PAGE_SIZE));
                    // Reset baseline so future realtime items accumulate from current head
                    if (activities[0]) {
                      firstActivityIdRef.current = activities[0].id;
                    }
                    // Scroll to top of the main element
                    const mainElement = document.querySelector(".pull-to-refresh-container") as HTMLElement;
                    if (mainElement) {
                      mainElement.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className="flex items-center gap-1 hover:opacity-90 transition-opacity"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="whitespace-nowrap">{newActivitiesCount} new</span>
                </button>
                <div className="w-px h-3 bg-primary opacity-20"></div>
                <button
                  onClick={() => {
                    setIsNewActivitiesBannerDismissed(true);
                    // Reset baseline to current head so dismissed items stay hidden until newer ones arrive
                    if (activities[0]) {
                      firstActivityIdRef.current = activities[0].id;
                    }
                  }}
                  className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
                  aria-label="Dismiss"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
      <main className="bg-white pb-20 relative main-content pull-to-refresh-container flex-1 overflow-y-auto">
        {/* Initial loading state */}
        {(isLoadingMore || isLoadingMoreActivities || isLoading) && (filteredActivities.length === 0 || activities.length === 0) && (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="animate-spin mb-4">
              <SparklesIcon className="w-12 h-12 text-primary" />
            </div>
            <p className="text-lg font-semibold text-gray-700">Loading activities...</p>
            <p className="text-sm text-gray-500 mt-2">Finding fun things to do</p>
          </div>
        )}
        {/* Progressive whoisin logo refresh */}
        <motion.div
          className="fixed top-20 inset-x-0 z-50 flex justify-center pointer-events-none"
          initial={{ y: -100, opacity: 0 }}
          animate={{
            y: pullDistance > 0 ? 0 : -100,
            opacity: pullDistance > 0 || refreshing ? 1 : 0,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            opacity: { duration: 0.2 },
          }}
        >
          <div className="relative w-12 h-12">
            {/* Background circle */}
            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48">
              <circle
                cx="24"
                cy="24"
                r="20"
                stroke="rgb(229, 231, 235)"
                strokeWidth="2"
                fill="none"
              />
              {/* Progress circle */}
              <motion.circle
                cx="24"
                cy="24"
                r="20"
                stroke="rgb(99, 102, 241)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 1 }}
                animate={{
                  pathLength: Math.min(pullDistance / 80, 1),
                  opacity: 1,
                }}
                transition={{
                  duration: 0.1,
                  ease: "easeOut",
                }}
                style={{
                  pathLength: Math.min(pullDistance / 80, 1),
                }}
              />
            </svg>
            {/* Progressive whoisin logo */}
            <div className="absolute inset-0 flex items-center justify-center">
              {refreshing ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  <AppLogoIcon className="w-6 h-6" />
                </motion.div>
              ) : (
                <div className="relative w-6 h-6">
                  <svg className="w-6 h-6" viewBox="0 0 100 125" fill="none">
                    <defs>
                      <linearGradient
                        id="progressGradient"
                        x1="50"
                        y1="0"
                        x2="50"
                        y2="125"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="rgb(139, 92, 246)" />
                        <stop offset="1" stopColor="rgb(124, 58, 237)" />
                      </linearGradient>
                    </defs>
                    {/* Main teardrop/pin shape - draws first */}
                    <motion.path
                      d="M50 0C22.386 0 0 22.386 0 50C0 87.5 50 125 50 125S100 87.5 100 50C100 22.386 77.614 0 50 0Z"
                      stroke="url(#progressGradient)"
                      strokeWidth="2"
                      fill="none"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: Math.min(pullDistance / 40, 1),
                        opacity: pullDistance > 5 ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.1,
                        ease: "easeOut",
                      }}
                    />
                    {/* Question mark curve - draws second */}
                    <motion.path
                      d="M44.375 60.625C44.375 56.8422 46.8422 53.75 50 53.75C53.1578 53.75 55.625 56.8422 55.625 60.625C55.625 62.9038 54.4069 64.918 52.5 66.25L50 72.5"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      fill="none"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: Math.min((pullDistance - 40) / 25, 1),
                        opacity: pullDistance > 40 ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.1,
                        ease: "easeOut",
                      }}
                    />
                    {/* Question mark dot - appears last */}
                    <motion.circle
                      cx="50"
                      cy="80"
                      r="1.25"
                      fill="white"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: pullDistance > 65 ? 1 : 0,
                        opacity: pullDistance > 65 ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.1,
                        ease: "easeOut",
                      }}
                    />
                    {/* Fill the teardrop when complete */}
                    <motion.path
                      d="M50 0C22.386 0 0 22.386 0 50C0 87.5 50 125 50 125S100 87.5 100 50C100 22.386 77.614 0 50 0Z"
                      fill="url(#progressGradient)"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: pullDistance >= 80 ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: "easeOut",
                      }}
                    />
                  </svg>
                  {/* Complete logo when ready */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{
                      scale: pullDistance >= 80 ? 1.2 : 1,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                  >
                    {pullDistance >= 80 && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 25,
                        }}
                      >
                        <AppLogoIcon className="w-6 h-6" />
                      </motion.div>
                    )}
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Activity content - only show when not in initial loading state */}
        {(!isLoading || activities.length > 0) && (
          <div className="w-full max-w-3xl mx-auto">
            {/* Stable container to prevent layout shifts during refresh */}
            <motion.div
              className="transition-all duration-300"
              initial={{ y: 0, opacity: 1 }}
              animate={{
                y: pullDistance > 0 ? pullDistance * 0.4 : 0,
                opacity: 1,
              }}
              transition={{
                type: "spring",
                stiffness: pullDistance > 0 ? 150 : 200,
                damping: pullDistance > 0 ? 20 : 25,
                duration: 0.3,
              }}
            >
              {/* Activity grid */}
              <div className="space-y-3 px-2 sm:px-0">
                <AnimatePresence mode="popLayout">
                  {visibleActivities.slice(0, visibleCount).map((activity) => (
                    <motion.div
                      key={activity.id}
                      layout
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -12, scale: 0.98, filter: "blur(2px)" }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <ActivityCard
                        activity={activity}
                        onPass={handlePassActivity}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Load More button or loading indicator */}
              {visibleCount < visibleActivities.length && (
                <div className="flex justify-center py-6">
                  {isLoadingMoreActivities ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin">
                        <SparklesIcon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-gray-600 text-sm">Loading more...</span>
                    </div>
                  ) : (
                    <button
                      className="px-6 py-2 rounded-full bg-primary text-white font-semibold shadow hover:bg-primary/90 transition-colors"
                      onClick={() => {
                        setIsLoadingMoreActivities(true);
                        setVisibleCount((prev) =>
                          Math.min(visibleActivities.length, prev + PAGE_SIZE),
                        );
                        setTimeout(() => setIsLoadingMoreActivities(false), 300);
                      }}
                    >
                      Load More Activities
                    </button>
                  )}
                </div>
              )}

              {/* Load More from server button */}
              {visibleCount >= visibleActivities.length && hasMoreActivities && !isLoadingMore && (
                <div className="flex justify-center py-6">
                  <button
                    className="px-6 py-2 rounded-full bg-primary text-white font-semibold shadow hover:bg-primary/90 transition-colors"
                    onClick={() => loadMoreActivities?.({ category: queryCategory })}
                  >
                    Load More
                  </button>
                </div>
              )}

              {/* Loading indicator when fetching from server */}
              {isLoadingMore && (
                <div className="flex justify-center py-6">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin">
                      <SparklesIcon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-gray-600 text-sm">Fetching more activities...</span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* End of feed container */}
        {/* Show empty state if no activities */}
        {!isLoading && !isLoadingMore && !hasMoreActivities && visibleActivities.length === 0 && (
          <div className="text-center py-20 px-4 flex flex-col items-center">
            <SparklesIcon className="w-16 h-16 text-primary/50 mb-4" />
            <p className="text-lg font-semibold text-gray-500">
              All caught up!
            </p>
            <p className="text-gray-500 mt-1">
              Check back later or try a different category.
            </p>
          </div>
        )}
        {/* End of feed container */}
        {/* Show empty state if no activities */}
        {!isLoading && !isLoadingMore && !hasMoreActivities && visibleActivities.length !== 0 && (
          <div className="text-center py-20 px-4 flex flex-col items-center">
            <SparklesIcon className="w-16 h-16 text-primary/50 mb-4" />
            <p className="text-lg font-semibold text-gray-500">
              All caught up!
            </p>
          </div>
        )}
      </main>
      {/* User Search Modal */}
      <Portal>
        <UserSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
        />
      </Portal>
    </div>
  );
};

export default HomePage;
